import { inject, Injectable } from '@angular/core';
import { ActionSheetController, ToastController } from '@ionic/angular';
import { ToastService } from './toast.service';
import { TranslationService } from '../translate-service/translation.service';

@Injectable({
    providedIn: 'root',
})
/**
 * Service for presenting toast.
 * Logic is split out into a service so async methods can be executed even after a component is destroyed.
 */
export class MobileToastService extends ToastService {
    private readonly toastController = inject(ToastController);
    private readonly actionSheetCtrl = inject(ActionSheetController);

    /**
     * Displays a toast message on the screen.
     *
     * @param {string} message The message to display in the toast.
     * @param {'success' | 'danger'} color The color of the toast, indicating the type of message.
     */
    async displayToast(
        message: string,
        color: 'success' | 'danger' | 'warning' | 'info',
        icon?: string,
        translucent?: boolean,
        duration = 2000,
    ): Promise<void> {
        const toast = await this.toastController.create({
            message,
            duration,
            position: 'top',
            color,
            icon,
            translucent,
        });
        await toast.present();
    }

    override async showSuccess(message: string): Promise<void> {
        await this.displayToast(message, 'success');
    }

    override async showError(message: string): Promise<void> {
        if (this.isRepeatOfStoreOffline(message)) {
            return;
        }
        await this.displayToast(message, 'danger');
    }

    override async showYesOrNoQuestion(title: string, content: string): Promise<boolean> {
        const translationService = this.injector.get(TranslationService);
        const toast = await this.actionSheetCtrl.create({
            header: title,
            subHeader: content,
            buttons: [
                {
                    text: translationService.getTranslation('Common', 'Button', 'No'),
                    role: 'cancel',
                    handler: () => {
                        return false;
                    },
                },
                {
                    text: translationService.getTranslation('Common', 'Button', 'Yes'),
                    role: 'confirm',
                    handler: () => {
                        return true;
                    },
                },
            ],
        });

        toast.present();

        return toast.onDidDismiss().then((result) => {
            return result.role === 'confirm' ? true : false;
        });
    }
    override async showDialog(title: string, content: string, actions: { text: string; value: boolean }[]): Promise<object> {
        const toast = await this.toastController.create({
            message: content,
            position: 'top',
            header: title,
            duration: 0,
            buttons: actions.map((action: { text: string; value: boolean }) => ({
                text: action.text,
                handler: () => {
                    return action.value;
                },
            })),
        });

        await toast.present();
        const result = await toast.onDidDismiss();
        return result.data || {};
    }
}
